// Central API client for the VentES AI Dashboard backend

const DEFAULT_API_URL = window.location.hostname === 'dashboard.ventes.group'
  ? 'https://ssdashapi.ventes.group'
  : 'http://localhost:8000';
const API_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token;
  }

  async fetch(path, { skipAuthRedirect = false, ...options } = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401 && !skipAuthRedirect) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth
  async getLoginUrl() {
    return this.fetch('/api/auth/login-url');
  }

  async exchangeCode(code) {
    return this.fetch('/api/auth/callback', {
      method: 'POST',
      skipAuthRedirect: true,
      body: JSON.stringify({ code }),
    });
  }

  async getMe() {
    return this.fetch('/api/auth/me');
  }

  // Chat
  async sendMessage(message, sessionId = null, activeApps = []) {
    return this.fetch('/api/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        session_id: sessionId,
        active_apps: activeApps,
      }),
    });
  }

  async getChatSessions() {
    return this.fetch('/api/chat/sessions');
  }

  async getSessionMessages(sessionId) {
    return this.fetch(`/api/chat/sessions/${sessionId}/messages`);
  }

  async deleteChatSession(sessionId) {
    return this.fetch(`/api/chat/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async confirmAction(actionId) {
    return this.fetch(`/api/actions/${actionId}/confirm`, { method: 'POST' });
  }

  async rejectAction(actionId) {
    return this.fetch(`/api/actions/${actionId}/reject`, { method: 'POST' });
  }

  async retryAction(actionId) {
    return this.fetch(`/api/actions/${actionId}/retry`, { method: 'POST' });
  }

  async getActions(status = null) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.fetch(`/api/actions${query}`);
  }

  // Reports
  async saveReport(title, reportType, data) {
    return this.fetch('/api/reports', {
      method: 'POST',
      body: JSON.stringify({ title, report_type: reportType, data }),
    });
  }

  async getReports() {
    return this.fetch('/api/reports');
  }

  async getReport(reportId) {
    return this.fetch(`/api/reports/${reportId}`);
  }

  async deleteReport(reportId) {
    return this.fetch(`/api/reports/${reportId}`, { method: 'DELETE' });
  }

  // Proxy (VentES app data)
  async listApps() {
    return this.fetch('/api/proxy');
  }

  async getAgentConnections() {
    return this.fetch('/api/agent-connections');
  }

  async createAgentConnection(name, scopes) {
    return this.fetch('/api/agent-connections', {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    });
  }

  async revokeAgentConnection(connectionId) {
    return this.fetch(`/api/agent-connections/${connectionId}`, { method: 'DELETE' });
  }
}

const api = new ApiClient();
export default api;
